import React, {useEffect} from 'react';
import {useSelector} from 'react-redux';

import AemEditableText from '@/components/Aem/AemEditableText/AemEditableText';
import AppButton from '@/components/Button/AppButton/AppButton';
import {TncDataCollectionIcon} from '@/components/Icons';
import {StickyPageLayout} from '@/components/Layout';
import {
    DATA_COLLECTION_HEADER_TITLE,
    TNC_DATA_COLLECTION_PAGE_AGREE_BUTTON_ARIA_LABEL,
    TNC_DATA_COLLECTION_PAGE_DECLINE_BUTTON_ARIA_LABEL,
    TNC_DATA_COLLECTION_PAGE_PREFERENCE_CENTER_BUTTON_ARIA_LABEL,
} from '@/consts/localization/localizationKeys';
import routePaths from '@/consts/route/routePaths';
import {WEB_PREFERENCE_CENTER} from '@/consts/tnc/tncTypes';
import useIsMobileLayout from '@/hooks/layout/useIsMobileLayout';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import useTermsOfUsePopup from '@/hooks/useTermsOfUsePopup';
import {getAriaLabel} from '@/services/accessibilityService';
import amplitudeProfileTracking from '@/services/analytics/amplitude/amplitudeProfileTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {getLocalizedStrings} from '@/services/localization/localizationService';
import appRouterService from '@/services/route/appRouterService';
import tncService from '@/services/tncService';
import {makeSelectAcceptedTncByType, makeSelectTncByType} from '@/state/selectors/consumer';
import {hideLoader, showLoader} from '@/state/slices/loadersSlice';
import {dispatch} from '@/state/store';
import TermsOfUseDescription from '@/views/Tnc/TermsOfUseDescription/TermsOfUseDescription';
import {WARNING_BANNER_SOURCE_TYPES, WarningBanners} from '@/views/WarningBanners';

import TncPageContainer from '../TncPageContainer';

const DataCollectionPage = () => {
    const isMobileLayout = useIsMobileLayout();
    const dataCollectionTnc = useSelector(makeSelectTncByType(WEB_PREFERENCE_CENTER));
    const acceptedTermsOfUse = useSelector(makeSelectAcceptedTncByType(WEB_PREFERENCE_CENTER));

    const {openPopup} = useTermsOfUsePopup();
    const isUpdatedTnc = !!acceptedTermsOfUse;
    const {description, legals, updateDescription} = dataCollectionTnc || {};
    const localizedStrings = getLocalizedStrings();

    useDocumentTitle(localizedStrings[DATA_COLLECTION_HEADER_TITLE]);

    useEffect(() => {
        amplitudeProfileTracking.trackUpdateYourDataScreen();
        googleAnalyticsService.trackUpdateYourDataScreenView();
    }, []);

    useEffect(() => {
        if (!isUpdatedTnc) {
            appRouterService.forwardToTncDetailsPage(WEB_PREFERENCE_CENTER);
            return;
        }

        if (dataCollectionTnc && legals) return;

        appRouterService.forwardToHomePageIfNeed();
    }, [dataCollectionTnc, legals]);

    const setTnc = async (isAccepted) => {
        dispatch(showLoader());

        await tncService.setDataCollectionTnc(legals, isAccepted);

        dispatch(hideLoader());
    };

    const onAcceptButtonClick = () => setTnc(true);
    const onDeclineButtonClick = () => setTnc(false);

    const descriptionText = isUpdatedTnc ? updateDescription : description;

    return (
        <TncPageContainer>
            <StickyPageLayout
                className='items-center'
                desktopClassName='w-[506px] justify-center py-5 flex-1 pt-5-hw-offset md:pb-8'
                desktopContentClassName='flex flex-col items-center'
                desktopFooterClassName='mt-6 md:mt-8'
                headerOptions={{
                    title: localizedStrings[DATA_COLLECTION_HEADER_TITLE],
                }}
                contentClassName='flex flex-1 flex-col items-center pt-safe-offset-sticky-header'
                footer={
                    <>
                        <div className='flex flex-col gap-4'>
                            <AppButton
                                className='mt-0'
                                primary
                                light
                                aria-label={getAriaLabel(TNC_DATA_COLLECTION_PAGE_AGREE_BUTTON_ARIA_LABEL)}
                                onClick={onAcceptButtonClick}
                            >
                                <AemEditableText pageName={routePaths.TNC} itemName='dataCollectionAgree' />
                            </AppButton>
                            <AppButton
                                className='mt-0'
                                secondary
                                light
                                aria-label={getAriaLabel(TNC_DATA_COLLECTION_PAGE_DECLINE_BUTTON_ARIA_LABEL)}
                                onClick={onDeclineButtonClick}
                            >
                                <AemEditableText pageName={routePaths.TNC} itemName='dataCollectionDecline' />
                            </AppButton>
                            <AppButton
                                className='mx-auto mt-0'
                                tertiary
                                aria-label={getAriaLabel(TNC_DATA_COLLECTION_PAGE_PREFERENCE_CENTER_BUTTON_ARIA_LABEL)}
                                onClick={() => appRouterService.forwardToTncDetailsPage(WEB_PREFERENCE_CENTER)}
                            >
                                <AemEditableText pageName={routePaths.TNC} itemName='dataCollectionPreferenceCenter' />
                            </AppButton>
                        </div>
                    </>
                }
            >
                <div className='flex flex-1 flex-col items-center px-4'>
                    <TncDataCollectionIcon />
                    <h1 className='mt-9 text-28 md:mb-0 md:mt-4'>
                        {!isUpdatedTnc && <AemEditableText pageName={routePaths.TNC} itemName='dataCollectionTitle' />}
                        {isUpdatedTnc && (
                            <AemEditableText pageName={routePaths.TNC} itemName='dataCollectionTitleUpdate' />
                        )}
                    </h1>
                    <TermsOfUseDescription
                        className='mt-4'
                        innerHtml={descriptionText}
                        tncType={WEB_PREFERENCE_CENTER}
                        onTncLinkClick={openPopup}
                    />
                </div>
                {isMobileLayout && <WarningBanners source={WARNING_BANNER_SOURCE_TYPES.TNC} className='mt-4' />}
            </StickyPageLayout>
            {!isMobileLayout && <WarningBanners className='-mt-4' source={WARNING_BANNER_SOURCE_TYPES.TNC} />}
        </TncPageContainer>
    );
};

export default DataCollectionPage;
